import { ArrowDown, ArrowUp, LayoutTemplate, Pencil, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  deleteNoticeStep,
  reorderNoticeSteps,
  updateNoticeStep,
} from "@/features/notices/api";
import { ApplyTemplateDialog } from "@/features/notices/components/apply-template-dialog";
import { NoticeStepDialog } from "@/features/notices/components/notice-step-dialog";
import type { Notice, NoticeStep } from "@/features/notices/types";

interface NoticeStepsPanelProps {
  notice: Notice;
  /** Refetch the notice after any step change (keeps counts fresh). */
  onChanged: () => void;
  /** Called when the last open step is completed, to offer "resolve notice?". */
  onAllStepsComplete: () => void;
}

/**
 * Action-steps panel for the notice detail page: complete/uncomplete, add,
 * edit (description + due date), reorder (accessible move up/down), delete, and
 * apply a firm step template. Mirrors `cases/CaseStepsPanel`.
 */
export function NoticeStepsPanel({
  notice,
  onChanged,
  onAllStepsComplete,
}: NoticeStepsPanelProps) {
  const noticeId = notice.id;
  const steps = notice.steps;
  const [dialog, setDialog] = useState<{ step: NoticeStep | null } | null>(null);
  const [deleting, setDeleting] = useState<NoticeStep | null>(null);
  const [templateOpen, setTemplateOpen] = useState(false);
  const [reordering, setReordering] = useState(false);

  async function toggle(step: NoticeStep) {
    const wasLastOpen =
      !step.isCompleted && steps.filter((s) => !s.isCompleted).length === 1;
    try {
      await updateNoticeStep(noticeId, step.id, { is_completed: !step.isCompleted });
      onChanged();
      if (wasLastOpen) onAllStepsComplete();
    } catch {
      toast.error("Couldn't update step.");
    }
  }

  async function move(index: number, direction: -1 | 1) {
    const target = index + direction;
    if (target < 0 || target >= steps.length || reordering) return;
    const ids = steps.map((s) => s.id);
    const a = ids[index]!;
    ids[index] = ids[target]!;
    ids[target] = a;
    setReordering(true);
    try {
      await reorderNoticeSteps(noticeId, ids);
      onChanged();
    } catch {
      toast.error("Couldn't reorder steps.");
    } finally {
      setReordering(false);
    }
  }

  async function confirmDelete() {
    if (!deleting) return;
    try {
      await deleteNoticeStep(noticeId, deleting.id);
      onChanged();
    } catch {
      toast.error("Couldn't remove step.");
    } finally {
      setDeleting(null);
    }
  }

  return (
    <div className="grid gap-4">
      <div className="flex items-center justify-between">
        <p className="text-muted-foreground text-sm">
          {steps.length === 0
            ? "No steps yet."
            : `${steps.filter((s) => !s.isCompleted).length} of ${steps.length} open`}
        </p>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => setTemplateOpen(true)}>
            <LayoutTemplate className="size-4" /> Apply Template
          </Button>
          <Button size="sm" onClick={() => setDialog({ step: null })}>
            <Plus className="size-4" /> Add Step
          </Button>
        </div>
      </div>

      {steps.length > 0 && (
        <ul className="flex flex-col gap-1">
          {steps.map((step, index) => (
            <li
              key={step.id}
              className="hover:bg-muted/40 flex items-center gap-3 rounded-md px-2 py-2"
            >
              <Checkbox
                checked={step.isCompleted}
                aria-label={`Mark "${step.description}" ${step.isCompleted ? "incomplete" : "complete"}`}
                onCheckedChange={() => void toggle(step)}
              />
              <div className="flex-1">
                <span
                  className={
                    step.isCompleted
                      ? "text-muted-foreground text-sm line-through"
                      : "text-foreground text-sm"
                  }
                >
                  {step.description}
                </span>
                {step.dueDate && (
                  <span className="text-muted-foreground ml-2 text-xs">
                    due {step.dueDate}
                  </span>
                )}
              </div>
              <Button
                variant="ghost"
                size="icon"
                aria-label={`Move "${step.description}" up`}
                disabled={index === 0 || reordering}
                onClick={() => void move(index, -1)}
              >
                <ArrowUp className="size-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                aria-label={`Move "${step.description}" down`}
                disabled={index === steps.length - 1 || reordering}
                onClick={() => void move(index, 1)}
              >
                <ArrowDown className="size-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                aria-label={`Edit "${step.description}"`}
                onClick={() => setDialog({ step })}
              >
                <Pencil className="size-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                aria-label={`Delete "${step.description}"`}
                onClick={() => setDeleting(step)}
              >
                <Trash2 className="text-destructive size-4" />
              </Button>
            </li>
          ))}
        </ul>
      )}

      {dialog && (
        <NoticeStepDialog
          key={dialog.step?.id ?? "new"}
          noticeId={noticeId}
          step={dialog.step}
          nextPosition={steps.length}
          open
          onOpenChange={(open) => !open && setDialog(null)}
          onSaved={onChanged}
        />
      )}

      <ApplyTemplateDialog
        noticeId={noticeId}
        open={templateOpen}
        onOpenChange={setTemplateOpen}
        onApplied={onChanged}
      />

      <AlertDialog
        open={deleting !== null}
        onOpenChange={(open) => !open && setDeleting(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove step?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes "{deleting?.description}" from the notice.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete}>Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
